<template>
    <div class="input-item">
        <label v-if="label" class="input-item__label">{{ label }}</label>

        <div class="input-item__wrapper">
            <input
                v-model="inputValue"
                type="text"
                class="input-item__input"
                autocomplete="false"
                :class="{
                    'input-item__wrapper__input-btn': includeButton,
                    'input-item--error': v$ && v$.$error
                }"
                :placeholder="placeholder"
                @update:modelValue="change"
            >

            <button
                v-if="includeButton"
                class="input-item__wrapper__btn"
                @click.prevent="includeButton.action"
            >
                {{ includeButton.label }}
            </button>
        </div>

        <div class="input-item__error" v-if="v$ && v$.$error">
            <span v-if="v$.inputValue.required" class="input-item__error__msg">{{ locale === 'en' ? 'This field is mandatory' : 'Fältet är obligatoriskt'}}</span>
            <span v-if="v$.inputValue.validationEmail" class="input-item__error__msg">{{ locale === 'en' ? 'Invalid email' : 'Felaktig e-postadress'}}</span>
            <span v-if="v$.inputValue.validationPhone" class="input-item__error__msg">{{ locale === 'en' ? 'Invalid phone number' : 'Ogiltigt telefonnummer'}}</span>
        </div>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, reactive, watchEffect, toRefs } from 'vue'
    import { required } from '@vuelidate/validators'
    import useVuelidate from '@vuelidate/core'
    import ValidationPhone from '@/validators/validation-phone'
    import ValidationEmail from '@/validators/validation-email'
    import { useI18n } from 'vue-i18n'

    export default defineComponent({
        props: {
            modelValue: {
                type: [String, Number],
                required: false
            },
            validators: {
                type: String,
                required: false
            },
            label: {
                type: String,
                required: false
            },
            placeholder: {
                type: String,
                required: false
            },
            type: {
                type: String,
                required: false,
                default: 'string'
            },
            includeButton: {
                type: Object,
                required: false
            }
        },
        emits: ['update:modelValue'],
        setup(props, { emit }) {
            const { locale } = useI18n()
            let v$ = useVuelidate()

            const hasValidation   = props.validators !== undefined
            const validationPhone = (value: string) => ValidationPhone.validate(value)
            const validationEmail = (value: string) => ValidationEmail.validate(value)

            const state = reactive({ inputValue: '' as string|number })

            const change = (value: string) => {
                if (!value) {
                    state.inputValue = ''
                    emit('update:modelValue', state.inputValue as string)

                    return
                }

                if (props.type === 'number') {
                    // Check if any letters
                    if (/[a-zA-Z]/g.test(value)) {
                        state.inputValue = ''
                        emit('update:modelValue', state.inputValue as string)
                    } else {
                        state.inputValue = parseInt(value.replace(/\s/g,''))
                        emit('update:modelValue', state.inputValue as number)
                    }
                } else {
                    emit('update:modelValue', state.inputValue as string)
                }

                if (hasValidation) {
                    touch()
                }
            }

            const reset = () => {
                if (hasValidation) {
                    state.inputValue = ''

                    v$.value.inputValue.$reset()
                }
            }

            const touch = () => v$.value.inputValue.$touch()

            if (hasValidation) {
                const RuleRequired = props.validators?.includes('required')
                const RuleEmail    = props.validators?.includes('email')
                const RulePhone    = props.validators?.includes('phone')

                let activeRoles: any = {}

                if (RuleRequired) {
                    activeRoles.inputValue = { required }
                }

                if (RuleEmail) {
                    activeRoles.inputValue = { validationEmail }
                }

                if (RulePhone) {
                    activeRoles.inputValue = { validationPhone }
                }

                const rules = computed(() => activeRoles)

                v$ = useVuelidate(rules, state)
            }

            watchEffect(() => {
                if (props.type === 'number') {
                    state.inputValue = props.modelValue ? Number(props.modelValue) as number : ''
                } else {
                    state.inputValue = props.modelValue as string
                }
            })

            return {
                ...toRefs(state),
                v$,
                change,
                reset,
                touch,
                locale
            }
        }
    })
</script>
